import React, { useEffect, useState } from 'react';

import { CompactMetric, DisclosurePanel } from '../components/AppShellPrimitives';
import MetricSparkline from '../components/MetricSparkline';
import { findingOccurrenceLabel } from '../appHelpers';
import { EvidencePostureChip } from '../evidencePosture';
import {
  EvidenceNeededGuidanceCard,
  buildFindingEvidenceGuidance,
} from '../evidenceNeededGuidance';
import SuspiciousTracePackPanel from './SuspiciousTracePackPanel';
import { RuntimeScenarioCandidateCard } from './RuntimeScenarioCandidateCard';

function formatPercent(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

function findingStatusLabel(status) {
  switch (status) {
    case 'in_progress':
      return 'In progress';
    case 'resolved':
      return 'Resolved';
    case 'open':
    default:
      return 'Open';
  }
}

function componentLabel(component) {
  if (!component) {
    return null;
  }
  return component.display_name || component.name;
}

function ownerLabel(owner) {
  if (!owner) {
    return 'Unassigned';
  }
  return owner.full_name || owner.email || owner.id;
}

function humanizeSnakeCase(value) {
  if (!value) {
    return '';
  }
  return String(value)
    .split('_')
    .filter(Boolean)
    .map((segment) => {
      const normalized = segment.toLowerCase();
      if (normalized === 'api') {
        return 'API';
      }
      if (normalized === 'llm') {
        return 'LLM';
      }
      if (normalized === 'io') {
        return 'I/O';
      }
      return segment.charAt(0).toUpperCase() + segment.slice(1);
    })
    .join(' ');
}

function pluralizeCount(value, singular, plural = `${singular}s`) {
  const safeValue = typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  return `${safeValue} ${safeValue === 1 ? singular : plural}`;
}

function formatReadableNumber(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'n/a';
  }
  const rounded = Math.abs(value) >= 10 ? Math.round(value * 10) / 10 : Math.round(value * 100) / 100;
  return `${rounded}`.replace(/\.0$/, '').replace(/(\.\d)0$/, '$1');
}

function formatSignalReading(value, unit) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'n/a';
  }
  if (unit === 'ratio') {
    return formatPercent(value);
  }
  const number = formatReadableNumber(value);
  return unit ? `${number} ${humanizeSnakeCase(unit).toLowerCase()}` : number;
}

const COST_INPUT_GUIDANCE = {
  'model pricing': 'No in-app pricing form exists yet. For alpha, include model, currency, and per-token pricing in runtime import or finding provenance metadata.',
  'workflow/request volume': 'No in-app volume form exists yet. For alpha, include request or run count plus period in runtime import or operator review metadata.',
  'external API unit pricing': 'No in-app pricing form exists yet. For alpha, include partner-provided unit pricing in provenance or operator review metadata.',
  'business-value context': 'No in-app business-case form exists yet. For alpha, capture partner-provided review, escalation, incident, or handoff cost in operator review metadata.',
};

function costRangeLabel(range) {
  if (!range) {
    return null;
  }
  const lower = range.lower_bound;
  const upper = range.upper_bound ?? lower;
  if (lower === undefined || lower === null) {
    return null;
  }
  const format = (value) => formatReadableNumber(value);
  if (upper === lower) {
    return format(lower);
  }
  return `${format(lower)} to ${format(upper)}`;
}

function costClaimValueLabel(claim) {
  const range = costRangeLabel(claim?.observed_value_range);
  if (!range) {
    return null;
  }
  return claim.observed_unit ? `${range} ${humanizeSnakeCase(claim.observed_unit).toLowerCase()}` : range;
}

function costInputGuidance(input) {
  return COST_INPUT_GUIDANCE[input] || 'Add this context as structured import, provenance, or operator review metadata.';
}

function costClaimTierLabel(claim) {
  switch (claim?.claim_tier) {
    case 'estimated_token_cost_per_run':
      return 'Pricing supplied';
    case 'estimated_period_spend':
      return 'Pricing and volume supplied';
    case 'business_cost':
      return 'Business context supplied';
    case 'observed_unit_impact':
    default:
      return 'Workflow units only';
  }
}

function costClaimHelper(claim) {
  switch (claim?.claim_tier) {
    case 'estimated_token_cost_per_run':
      return 'This is the strongest cost statement supported now: token cost per run, based on explicit pricing.';
    case 'estimated_period_spend':
      return 'This is the strongest cost statement supported now: period spend, based on explicit pricing and volume.';
    case 'business_cost':
      return 'This is the strongest cost statement supported now: business cost, based on partner-provided value context.';
    case 'observed_unit_impact':
    default:
      return 'This is current exposure evidence, not a savings claim. Use comparable trend movement to judge recovery.';
  }
}

function costClaimObjective(claim) {
  switch (claim?.observed_unit) {
    case 'tokens_per_run':
      return 'Lower tokens per run in a later comparable window while keeping workflow outcome quality stable.';
    case 'at_risk_runs_per_100':
      return 'Reduce repeat-work or failure-loop exposure while keeping validation quality stable.';
    case 'at_risk_io_steps_per_100':
      return 'Reduce retrieval or external-step risk while keeping workflow reliability stable.';
    case 'seconds_per_run':
      return 'Reduce latency in a later comparable window while keeping completion quality stable.';
    default:
      return 'Reduce this workflow unit in a later comparable window while keeping the outcome signal stable.';
  }
}

function CostEvidenceInputs({ inputs }) {
  if (!inputs?.length) {
    return null;
  }
  return (
    <div className="cost-input-list">
      <strong>How to strengthen this claim</strong>
      {inputs.map((input) => (
        <div className="cost-input-row" key={input}>
          <span>{input}</span>
          <button className="cost-info-button" type="button" aria-label={`${input}: ${costInputGuidance(input)}`}>
            i
          </button>
          <p className="cost-input-help">{costInputGuidance(input)}</p>
        </div>
      ))}
    </div>
  );
}

function CostClaimCard({ claim }) {
  if (!claim) {
    return null;
  }
  const valueLabel = costClaimValueLabel(claim);
  return (
    <div className="detail-card finding-detail-glance-card cost-claim-panel">
      <div className="cost-claim-header">
        <div>
          <span className="summary-card-label">Cost evidence</span>
          <strong>{claim.label}</strong>
        </div>
        <span className="chip cost-claim-chip">{costClaimTierLabel(claim)}</span>
      </div>
      <p className="cost-claim-helper">{costClaimHelper(claim)}</p>
      {valueLabel ? (
        <div className="cost-claim-value">
          <span>Observed unit</span>
          <strong>{valueLabel}</strong>
        </div>
      ) : null}
      <p>{claim.summary}</p>
      {claim.claim_tier === 'observed_unit_impact' ? (
        <p className="cost-claim-objective">
          <span className="cost-claim-objective-label">Aim</span>
          <span className="cost-claim-objective-copy">{costClaimObjective(claim)}</span>
        </p>
      ) : null}
      <CostEvidenceInputs inputs={claim.missing_inputs} />
      {claim.assumptions?.length ? <p className="overview-note">{claim.assumptions.join(' ')}</p> : null}
    </div>
  );
}

function impactRangeLabel(impactEstimate) {
  if (!impactEstimate) {
    return null;
  }
  const lower = impactEstimate.lower_bound;
  const upper = impactEstimate.upper_bound ?? lower;
  if (lower === undefined && upper === undefined) {
    return null;
  }
  if (upper === lower) {
    return `${upper}`;
  }
  return `${lower} to ${upper}`;
}

function findingSystemImpactSummary(finding) {
  const impactEstimate = finding?.impact_estimate;
  const range = impactRangeLabel(impactEstimate);
  if (!impactEstimate || !range) {
    return null;
  }

  switch (impactEstimate.unit) {
    case 'unguarded_actions_per_100_runs':
      return `About ${range} risky actions may proceed without validation in every 100 runs.`;
    case 'at_risk_runs_per_100':
      return `About ${range} runs in every 100 may be at risk of failure or instability.`;
    case 'at_risk_io_steps_per_100':
      return `About ${range} retrieval or external steps in every 100 may be at risk.`;
    case 'seconds_per_run':
      return `Affected runs may take about ${range} seconds longer to complete.`;
    case 'tokens_per_run':
      return `The workflow may spend about ${range} extra tokens per run.`;
    case 'extra_steps_per_run':
      return `The workflow may perform about ${range} extra steps each run.`;
    case 'extra_loop_steps_per_run':
      return `The workflow may spend about ${range} extra loop steps before it can finish.`;
    case 'human_handoffs_per_100_runs':
      return `About ${range} runs in every 100 may still require human handoff.`;
    case 'premature_handoffs_per_100_runs':
      return `About ${range} runs in every 100 may hand off to a person earlier than necessary.`;
    case 'invalid_sequences_per_100_runs':
      return `About ${range} runs in every 100 may enter an invalid or risky sequence.`;
    case 'rescued_runs_per_100':
      return `About ${range} runs in every 100 depend on fallback recovery to complete.`;
    case 'rewrite_dependent_runs_per_100':
      return `About ${range} runs in every 100 depend on hidden query rewrites to succeed.`;
    case 'irrelevant_docs_per_100_retrieved':
      return `About ${range} of every 100 retrieved documents may be low-value or irrelevant.`;
    case 'screening_calls_per_kept_doc':
      return `Keeping one useful document may require about ${range} screening passes.`;
    case 'uncited_answers_per_100_runs':
      return `About ${range} answer outputs in every 100 may surface without citations even when evidence is attached.`;
    case 'weakly_grounded_answers_per_100_runs':
      return `About ${range} answer outputs in every 100 may be weakly supported by evidence.`;
    case 'misclassified_successes_per_100_runs':
      return `About ${range} runs in every 100 may have a completed technical status despite conflicting terminal evidence.`;
    case 'reanalysed_source_items_per_100':
      return `About ${range} source items in every 100 may be re-analysed instead of carrying context forward.`;
    default: {
      const unitLabel = humanizeSnakeCase(impactEstimate.unit).toLowerCase();
      return unitLabel
        ? `Estimated system impact is about ${range} ${unitLabel}.`
        : `Estimated system impact is about ${range}.`;
    }
  }
}

function evidenceBaseSummary(evidenceSummary) {
  if (!evidenceSummary) {
    return 'Grounding details will appear after more runtime evidence is linked.';
  }
  return `Grounded in ${pluralizeCount(evidenceSummary.observation_count, 'observation')}, ${pluralizeCount(
    evidenceSummary.signal_count,
    'signal'
  )}, and ${pluralizeCount(evidenceSummary.evidence_link_count, 'linked runtime reference')}.`;
}

function findingReferenceLabel(finding) {
  if (finding?.priority_rank) {
    return `F-${String(finding.priority_rank).padStart(2, '0')}`;
  }
  return 'Finding';
}

function signalDisplayName(signal) {
  return humanizeSnakeCase(signal?.metric_name || signal?.signal_type || 'signal');
}

function signalCalculationDescription(signal) {
  switch (signal?.metric_name || signal?.signal_type) {
    case 'missing_validation_rate':
      return 'Calculated as the share of risky tool or external API actions that happened without a validation step in the previous two actions.';
    case 'invalid_sequence_rate':
      return 'Calculated as the share of runs where risky actions appear before nearby validation evidence.';
    case 'premature_handoff_rate':
      return 'Calculated as the share of runs handed to a person shortly after a failed or timed-out step with little retry.';
    case 'retrieval_fallback_rate':
      return 'Calculated as the share of runs that only reach usable evidence after a fallback retrieval rescues an earlier weak attempt.';
    case 'weak_evidence_answer_rate':
      return 'Calculated as the share of completed answer runs where retrieval returned no or minimal useful evidence.';
    case 'uncited_answer_with_evidence_rate':
      return 'Calculated as the share of answer outputs that include evidence objects but surface no citations.';
    case 'clarification_dependency_rate':
      return 'Calculated as the share of eligible runs that need another clarification turn before they can continue.';
    case 'query_rewrite_dependency_rate':
      return 'Calculated as the share of successful runs that depend on a rewritten query before downstream retrieval or tool use.';
    case 'retrieval_precision_loss_rate':
      return 'Calculated as the share of retrieved documents that were judged irrelevant.';
    case 'relevance_screening_overhead':
      return 'Calculated as the amount of screening effort spent for each useful document that is kept.';
    case 'retrieval_attempt_depth':
      return 'Calculated as the median number of retrieval or screening rounds before the run resolves.';
    default:
      return `Calculated from the ${signalDisplayName(signal).toLowerCase()} signal across the current analysis window.`;
  }
}

function comparisonTrendSummary(findingComparison) {
  if (!findingComparison?.comparison_available) {
    return findingComparison?.reason || 'Not enough follow-up runtime data is available yet to compare before and after.';
  }
  const baseline = formatSignalReading(findingComparison?.baseline?.metric_value, findingComparison?.unit);
  const current = formatSignalReading(findingComparison?.current?.metric_value, findingComparison?.unit);
  const direction = findingComparison?.improvement?.direction;

  if (baseline === 'n/a' || current === 'n/a') {
    return findingComparison?.summary || 'A recent comparison is available.';
  }
  if (direction === 'improved') {
    return `The latest comparable window improved from ${baseline} to ${current}.`;
  }
  if (direction === 'regressed') {
    return `The latest comparable window regressed from ${baseline} to ${current}.`;
  }
  return `The latest comparable window moved from ${baseline} to ${current}.`;
}

function formatHistoryDate(value) {
  if (!value) {
    return 'n/a';
  }
  return new Date(value).toLocaleString();
}

function trendCaveatLabel(point) {
  const caveats = point?.caveats || [];
  if (point?.low_data_reason) {
    return point.low_data_reason;
  }
  if (point?.stale_reason) {
    return point.stale_reason;
  }
  if (caveats.includes('analysis_version_boundary')) {
    return 'This point crosses an analysis-version boundary.';
  }
  if (caveats.includes('snapshot_only')) {
    return 'This point comes from a persisted comparison snapshot.';
  }
  if (caveats.includes('unknown_evidence_lineage')) {
    return 'Evidence lineage is unavailable for this historical point.';
  }
  return '';
}

function ChapterHeader({ kicker, title }) {
  return (
    <div className="chapter-header">
      <div>
        <p className="chapter-kicker">{kicker}</p>
        <h3>{title}</h3>
      </div>
    </div>
  );
}

function FindingTriageBrief({ findingDetail, systemImpactSummary }) {
  const primaryObservation = findingDetail.observations?.[0] || null;
  const primarySignal = findingDetail.signals?.[0] || null;
  const whyTitle = primaryObservation?.summary || signalDisplayName(primarySignal) || 'Runtime evidence needs review';
  const whyCopy =
    primaryObservation?.details ||
    (primarySignal ? signalCalculationDescription(primarySignal) : evidenceBaseSummary(findingDetail.evidence_summary));
  const signalReading = primarySignal ? formatSignalReading(primarySignal.metric_value, primarySignal.unit) : null;

  return (
    <section className="finding-triage-brief" aria-label="Impact and rationale">
      <div className="finding-triage-copy-grid">
        <article className="finding-brief-card finding-brief-impact-card">
          <span className="summary-card-label">Impact</span>
          <strong>{systemImpactSummary || findingDetail.finding.impact_summary || 'Impact estimate pending'}</strong>
        </article>
        <article className="finding-brief-card">
          <span className="summary-card-label">Why this was raised</span>
          <strong>{whyTitle}</strong>
          <p>{whyCopy}</p>
        </article>
      </div>
      <div className="finding-triage-meta-strip" aria-label="Finding review state">
        <span className="chip chip-emphasis">{findingStatusLabel(findingDetail.finding.status)}</span>
        <span className="chip">{findingDetail.finding.severity}</span>
        <EvidencePostureChip posture={findingDetail.evidence_posture} prefix="Freshness" />
        <CompactMetric label="Confidence" value={formatPercent(findingDetail.finding.confidence)} tone="muted" />
        <CompactMetric label="Occurrence" value={findingOccurrenceLabel(findingDetail.finding)} tone="muted" />
        {signalReading ? <CompactMetric label="Signal" value={signalReading} tone="muted" /> : null}
      </div>
    </section>
  );
}

function referencePostureLabel(posture) {
  switch (posture) {
    case 'in_app_auto_check':
      return 'Auto-checkable';
    case 'human_review_required':
      return 'Human review';
    case 'external_evaluation_required':
      return 'External evaluation';
    case 'diagnostic_only':
      return 'Diagnostic only';
    default:
      return posture ? humanizeSnakeCase(posture) : 'Reference guidance';
  }
}

function expectedMovementLabel(direction) {
  switch (direction) {
    case 'decrease':
      return 'Should decrease';
    case 'increase':
      return 'Should increase';
    case 'stabilize':
      return 'Should stabilize';
    default:
      return direction ? humanizeSnakeCase(direction) : 'Expected movement pending';
  }
}

function ReferenceList({ title, items }) {
  if (!items?.length) {
    return null;
  }
  return (
    <div className="eval-pack-guidance-block">
      <strong>{title}</strong>
      <ul className="eval-pack-guidance-list">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function ReferenceFindingPanel({ reference }) {
  if (!reference) {
    return null;
  }
  return (
    <article className="detail-card finding-detail-reference-card">
      <div className="workflow-mode-header">
        <div>
          <span className="summary-card-label">Reference finding</span>
          <strong>{reference.title}</strong>
        </div>
        <span className="chip chip-emphasis">{referencePostureLabel(reference.validation_posture)}</span>
      </div>
      <p>{reference.partner_explanation}</p>
      <div className="finding-meta-grid">
        <div className="finding-meta-item">
          <span>Detected signal</span>
          <strong>{humanizeSnakeCase(reference.primary_signal)}</strong>
        </div>
        <div className="finding-meta-item">
          <span>Compare metric</span>
          <strong>{reference.compare_metric_label || humanizeSnakeCase(reference.compare_metric)}</strong>
        </div>
        <div className="finding-meta-item">
          <span>Expected movement</span>
          <strong>{expectedMovementLabel(reference.expected_movement_direction)}</strong>
        </div>
      </div>
      <div className="stack compact-stack eval-pack-guidance">
        <ReferenceList title="Evidence expectations" items={reference.evidence_expectations} />
        <ReferenceList title="Overclaim limits" items={reference.overclaim_limits} />
      </div>
    </article>
  );
}

function planStatusLabel(status) {
  switch (status) {
    case 'awaiting_post_fix_evidence':
      return 'Awaiting evidence';
    case 'review_ready':
      return 'Review ready';
    case 'monitoring':
      return 'Monitoring';
    case 'closed':
      return 'Closed';
    case 'archived':
      return 'Archived';
    case 'planned':
    default:
      return 'Planned';
  }
}

function planOutcomeLabel(outcomeState) {
  switch (outcomeState) {
    case 'insufficient_comparable_evidence':
      return 'Insufficient comparable evidence';
    case 'improved_in_latest_evidence':
      return 'Improved in latest evidence';
    case 'supported_by_validation':
      return 'Supported by validation';
    case 'regression_risk_remains':
      return 'Regression risk remains';
    case 'no_clear_improvement':
      return 'No clear improvement';
    case 'not_reviewed':
    default:
      return '';
  }
}

function latestSuccessfulExport(recentExports) {
  return (recentExports || []).find((item) => item.status === 'succeeded') || (recentExports || [])[0] || null;
}

function validationStatusLabel(targetedValidation) {
  if (!targetedValidation) {
    return '';
  }
  if (targetedValidation.latest_result_summary || targetedValidation.result_run_count > 0) {
    return 'Review validation results';
  }
  if (targetedValidation.active_pack_id) {
    return 'Review validation pack';
  }
  if (targetedValidation.eligibility_status === 'eligible') {
    return 'Preview validation pack';
  }
  return '';
}

function ImprovementPlanCard({
  selectedSystem,
  findingDetail,
  improvementPlanSaving,
  improvementPlanError,
  onSaveImprovementPlan,
  section,
}) {
  const activePlan = findingDetail.active_improvement_plan;
  const activePackId = findingDetail.targeted_validation?.active_pack_id || activePlan?.eval_pack_id || '';
  const defaultExport = latestSuccessfulExport(findingDetail.recent_exports);
  const exportId = defaultExport?.id || activePlan?.finding_export_id || '';
  const [intendedFixSummary, setIntendedFixSummary] = useState('');
  const [expectedMovement, setExpectedMovement] = useState('');
  const [status, setStatus] = useState('planned');
  const [outcomeState, setOutcomeState] = useState('not_reviewed');
  const [outcomeSummary, setOutcomeSummary] = useState('');
  const [linkPack, setLinkPack] = useState(false);
  const [linkExport, setLinkExport] = useState(false);

  useEffect(() => {
    setIntendedFixSummary(activePlan?.intended_fix_summary || '');
    setExpectedMovement(activePlan?.expected_signal_movement?.summary || '');
    setStatus(activePlan?.status || 'planned');
    setOutcomeState(activePlan?.outcome_state || 'not_reviewed');
    setOutcomeSummary(activePlan?.outcome_summary || '');
    setLinkPack(Boolean(activePlan?.eval_pack_id || activePackId));
    setLinkExport(Boolean(activePlan?.finding_export_id || exportId));
  }, [activePlan, activePackId, exportId]);

  const reviewedOutcomeLabel = activePlan?.outcome_state && activePlan.outcome_state !== 'not_reviewed'
    ? planOutcomeLabel(activePlan.outcome_state)
    : '';
  const displayStateLabel = reviewedOutcomeLabel || (activePlan ? planStatusLabel(activePlan.status) : 'Create plan');

  const savePlan = () => {
    const payload = {
      intended_fix_summary: intendedFixSummary.trim(),
      expected_signal_movement: expectedMovement.trim() ? { summary: expectedMovement.trim() } : null,
      eval_pack_id: linkPack && activePackId ? activePackId : null,
      finding_export_id: linkExport && exportId ? exportId : null,
    };
    if (activePlan) {
      payload.status = status;
      payload.outcome_state = outcomeState;
      payload.outcome_summary = outcomeSummary.trim() ? outcomeSummary.trim() : null;
    }
    onSaveImprovementPlan(findingDetail.finding.id, activePlan?.id || null, payload);
  };

  return (
    <div className="detail-card improvement-plan-card">
      <div className="workflow-mode-header">
        <div>
          <span className="summary-card-label">Improvement plan</span>
          <strong>{displayStateLabel}</strong>
          <p>
            {activePlan
              ? activePlan.outcome_summary || 'This plan records the intended change and the evidence still needed to review it.'
              : 'Capture the intended change before judging post-fix evidence.'}
          </p>
        </div>
        {activePlan ? <span className="chip chip-emphasis">{displayStateLabel}</span> : null}
      </div>
      <div className="stack compact-stack">
        <label className="form-field">
          <span>Intended fix</span>
          <textarea
            className="text-input"
            rows="3"
            value={intendedFixSummary}
            onChange={(event) => setIntendedFixSummary(event.target.value)}
            placeholder="Describe the remediation attempt the team plans to make."
          />
        </label>
        <label className="form-field">
          <span>Expected signal movement</span>
          <input
            className="text-input"
            value={expectedMovement}
            onChange={(event) => setExpectedMovement(event.target.value)}
            placeholder="Example: retrieval attempt depth should decrease."
          />
        </label>
        {activePlan ? (
          <div className="detail-summary-grid improvement-plan-form-grid">
            <label className="form-field">
              <span>Status</span>
              <select className="text-input" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="planned">Planned</option>
                <option value="awaiting_post_fix_evidence">Awaiting evidence</option>
                <option value="review_ready">Review ready</option>
                <option value="monitoring">Monitoring</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label className="form-field">
              <span>Outcome</span>
              <select className="text-input" value={outcomeState} onChange={(event) => setOutcomeState(event.target.value)}>
                <option value="not_reviewed">Not reviewed</option>
                <option value="insufficient_comparable_evidence">Insufficient comparable evidence</option>
                <option value="improved_in_latest_evidence">Improved in latest evidence</option>
                <option value="supported_by_validation">Supported by validation</option>
                <option value="regression_risk_remains">Regression risk remains</option>
                <option value="no_clear_improvement">No clear improvement</option>
              </select>
            </label>
          </div>
        ) : null}
        {activePlan ? (
          <label className="form-field">
            <span>Outcome summary</span>
            <textarea
              className="text-input"
              rows="2"
              value={outcomeSummary}
              onChange={(event) => setOutcomeSummary(event.target.value)}
            />
          </label>
        ) : null}
        <div className="chip-row improvement-plan-link-row">
          {activePackId ? (
            <label className="checkbox-row">
              <input type="checkbox" checked={linkPack} onChange={(event) => setLinkPack(event.target.checked)} />
              <span>Link active validation pack</span>
            </label>
          ) : null}
          {defaultExport ? (
            <label className="checkbox-row">
              <input type="checkbox" checked={linkExport} onChange={(event) => setLinkExport(event.target.checked)} />
              <span>Attach latest export</span>
            </label>
          ) : null}
        </div>
        {improvementPlanError ? <p className="overview-note">{improvementPlanError}</p> : null}
        <div className="form-actions">
          <button
            className="primary-button"
            type="button"
            disabled={improvementPlanSaving || !intendedFixSummary.trim()}
            onClick={savePlan}
          >
            {improvementPlanSaving ? 'Saving…' : activePlan ? 'Update plan' : 'Create plan'}
          </button>
          {activePlan?.eval_pack_id ? (
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/eval-packs/${activePlan.eval_pack_id}`}>
              Open validation
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function FindingDetailView({
  selectedSystem,
  findingDetail,
  findingComparison,
  banner,
  improvementPlanSaving,
  improvementPlanError,
  onSaveImprovementPlan,
  section,
}) {
  const systemImpactSummary = findingSystemImpactSummary(findingDetail.finding);
  const evidenceGuidance = buildFindingEvidenceGuidance(findingDetail, findingComparison);
  const metricHistory = findingComparison?.metric_history;
  const metricHistoryCaveatLabel = metricHistory?.has_analysis_version_boundary
    ? 'Analysis version boundary'
    : metricHistory?.caveat_count
      ? `${metricHistory.caveat_count} ${metricHistory.caveat_count === 1 ? 'caveat' : 'caveats'}`
      : '';
  const validationSummary = findingDetail.targeted_validation;
  const showImprovementPlan = Boolean(findingDetail.active_improvement_plan);
  const hasRuntimeScenario = findingDetail.runtime_scenario_candidate?.status === 'available';
  const sectionHref = (sectionId) => `#/systems/${selectedSystem.id}/findings/${findingDetail.finding.id}?section=${sectionId}`;
  const evaluationSummaryMeta = hasRuntimeScenario
    ? validationStatusLabel(validationSummary) || 'Scenario review'
    : 'Not available';
  const findingReference = findingReferenceLabel(findingDetail.finding);
  const tracePackAvailable = findingDetail.suspicious_trace_pack?.status === 'available';
  const currentAction = tracePackAvailable
    ? {
        href: sectionHref('trace-review-section'),
        label: 'Review trace evidence',
        helper: 'Inspect suspicious traces before stronger claims.',
      }
    : hasRuntimeScenario
      ? {
          href: sectionHref('targeted-validation-section'),
          label: 'Review evaluation handoff',
          helper: 'Check the scenario review and coverage state.',
        }
      : {
          href: sectionHref('finding-evidence-section'),
          label: 'Review evidence details',
          helper: 'Use the evidence caveats before deciding the next proof step.',
        };

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel finding-detail-panel">
        {banner}
        <div className="finding-detail-toolbar" id="finding-claim-section">
          <a className="finding-detail-back-button" href={`#/systems/${selectedSystem.id}/findings`} aria-label="Back to findings" title="Back to findings">
            <span aria-hidden="true">‹</span>
          </a>
          {findingDetail.finding.component ? (
            <a className="finding-link" href={`#/systems/${selectedSystem.id}/components/${findingDetail.finding.component.id}`}>
              Component: {componentLabel(findingDetail.finding.component)}
            </a>
          ) : null}
        </div>
        <div className="finding-case-header">
          <div className="finding-case-title">
            <div className="finding-card-meta">
              <span className="finding-reference-badge" aria-label={`Finding reference ${findingReference}`}>{findingReference}</span>
              <span className="finding-category-badge">{findingDetail.finding.primary_impact_category || 'analysis'}</span>
              <span className="finding-severity-label">{findingDetail.finding.severity}</span>
              <span className="finding-status-label">{findingStatusLabel(findingDetail.finding.status)}</span>
            </div>
            <h2 className="finding-detail-title">{findingDetail.finding.title}</h2>
            <p className="panel-copy finding-detail-intro">{findingDetail.finding.customer_language_summary || findingDetail.finding.summary}</p>
          </div>
          <aside className="finding-current-action" aria-label="Current finding action">
            <span className="summary-card-label">Current next action</span>
            <strong>{currentAction.label}</strong>
            <p>{currentAction.helper}</p>
            <a className="secondary-button action-link-button" href={currentAction.href}>
              Jump to step
            </a>
          </aside>
        </div>

        <FindingTriageBrief findingDetail={findingDetail} systemImpactSummary={systemImpactSummary} />

        <nav className="finding-notebook-nav" aria-label="Finding review sections">
          <a href={sectionHref('finding-claim-section')}>Claim</a>
          <a href={sectionHref('finding-evidence-section')}>Evidence</a>
          <a href={sectionHref('trace-review-section')}>Trace review</a>
          <a href={sectionHref('targeted-validation-section')}>Validation</a>
          <a href={sectionHref('finding-proof-section')}>Proof</a>
        </nav>

        <div className="calm-finding-sections">
        <SuspiciousTracePackPanel
          pack={findingDetail.suspicious_trace_pack}
          selectedSystem={selectedSystem}
          defaultOpen
          visibleRoles={['suspicious']}
          maxEntriesByRole={{ suspicious: 3 }}
        />

        <section className="finding-proof-summary detail-section" id="finding-proof-section" aria-label="Evaluation and proof state">
          <div>
            <span className="summary-card-label">Evaluation and proof state</span>
            <strong>{evaluationSummaryMeta}</strong>
            <p>
              {hasRuntimeScenario
                ? 'Scenario review and evaluation coverage are available below. Compare remains the proof surface once runtime movement and evaluation outcome can be read together.'
                : 'No runtime scenario review is ready yet. Review trace evidence and evidence gaps before moving toward coverage.'}
            </p>
          </div>
          <a className="secondary-button action-link-button" href={sectionHref('targeted-validation-section')}>
            Review validation
          </a>
        </section>

        <DisclosurePanel
          title="Evidence details"
          className="detail-section finding-evidence-details-disclosure"
          summaryMeta={evidenceBaseSummary(findingDetail.evidence_summary)}
        >
          <div className="detail-summary-grid finding-detail-summary-grid">
            <div className="detail-card finding-detail-glance-card">
              <span className="summary-card-label">At a glance</span>
              <div className="finding-meta-grid">
                <div className="finding-meta-item">
                  <span>Owner</span>
                  <strong>{ownerLabel(findingDetail.owner)}</strong>
                </div>
                <div className="finding-meta-item">
                  <span>Workflow</span>
                  <strong>{findingDetail.workflow?.name || 'Workflow context unavailable'}</strong>
                </div>
                {findingDetail.finding.component ? (
                  <div className="finding-meta-item">
                    <span>Component</span>
                    <strong>{componentLabel(findingDetail.finding.component)}</strong>
                  </div>
                ) : null}
                <div className="finding-meta-item">
                  <span>Evidence base</span>
                  <strong>{evidenceBaseSummary(findingDetail.evidence_summary)}</strong>
                </div>
              </div>
            </div>
          </div>
          <EvidenceNeededGuidanceCard
            title="Selective review evidence"
            canSay={evidenceGuidance.canSay}
            cannotSay={evidenceGuidance.cannotSay}
            nextSteps={evidenceGuidance.nextSteps}
          />
        </DisclosurePanel>

        <DisclosurePanel
          title="Evaluation"
          className="detail-section finding-evaluation-disclosure"
          id="targeted-validation-section"
          summaryMeta={evaluationSummaryMeta}
          defaultOpen={section === 'targeted-validation-section'}
        >
          <RuntimeScenarioCandidateCard
            candidate={findingDetail.runtime_scenario_candidate}
            selectedSystem={selectedSystem}
            targetedValidation={findingDetail.targeted_validation}
            embedded
            sectionId={null}
          />
        </DisclosurePanel>

        {findingDetail.finding.cost_claim ? (
          <DisclosurePanel title="Cost details" className="detail-section">
            <CostClaimCard claim={findingDetail.finding.cost_claim} />
          </DisclosurePanel>
        ) : null}

        {showImprovementPlan ? (
          <DisclosurePanel
            title="Improvement plan"
            className="detail-section"
            id="finding-improvement-plan"
            defaultOpen={Boolean(findingDetail.active_improvement_plan)}
          >
            <ImprovementPlanCard
              selectedSystem={selectedSystem}
              findingDetail={findingDetail}
              improvementPlanSaving={improvementPlanSaving}
              improvementPlanError={improvementPlanError}
              onSaveImprovementPlan={onSaveImprovementPlan}
            />
          </DisclosurePanel>
        ) : null}

        <DisclosurePanel title="Why this was raised" className="detail-section" id="finding-evidence-section">
          <ChapterHeader kicker="Why" title="Why This Was Raised" />
          <p className="panel-copy">Use the observation and signal below to understand what the analysis measured and why this finding was opened.</p>
          <ReferenceFindingPanel reference={findingDetail.finding.reference} />
          <div className="detail-summary-grid finding-detail-explainer-grid">
            {findingDetail.observations.map((observation) => (
              <article className="detail-card finding-detail-explainer-card" key={observation.id}>
                <span className="summary-card-label">Observation</span>
                <strong>{observation.summary}</strong>
                <p>{observation.details}</p>
              </article>
            ))}
            {findingDetail.signals.map((signal) => (
              <article className="detail-card finding-detail-explainer-card" key={signal.id}>
                <span className="summary-card-label">Signal</span>
                <strong>{signalDisplayName(signal)}</strong>
                <p>{signalCalculationDescription(signal)}</p>
                <div className="finding-meta-grid">
                  <div className="finding-meta-item">
                    <span>Current reading</span>
                    <strong>{formatSignalReading(signal.metric_value, signal.unit)}</strong>
                  </div>
                  <div className="finding-meta-item">
                    <span>Confidence</span>
                    <strong>{formatPercent(signal.confidence)}</strong>
                  </div>
                </div>
              </article>
            ))}
            {!findingDetail.observations.length && !findingDetail.signals.length ? (
              <div className="empty-state-card">
                <strong>No structured evidence summary yet</strong>
                <p>This finding can still be reviewed from its persisted summary and remediation guidance while structured evidence accumulates.</p>
              </div>
            ) : null}
          </div>
        </DisclosurePanel>

        {findingDetail.drivers.length ? (
          <DisclosurePanel title="Likely drivers" className="detail-section">
            <ChapterHeader kicker="Diagnosis" title="Likely Drivers" />
            <div className="detail-summary-grid finding-detail-driver-grid">
              {findingDetail.drivers.map((driver, index) => (
                <article className="detail-card finding-detail-driver-card" key={`${driver.driver_type}-${index}`}>
                  <strong>{driver.title}</strong>
                  <p>{driver.summary}</p>
                  <div className="chip-row">
                    {driver.workflow_name ? <span className="chip chip-emphasis">Workflow: {driver.workflow_name}</span> : null}
                    {driver.component_id ? (
                      <a className="chip chip-emphasis" href={`#/systems/${selectedSystem.id}/components/${driver.component_id}`}>
                        Component: {driver.component_name}
                      </a>
                    ) : null}
                    {typeof driver.contribution_ratio === 'number' ? (
                      <span className="chip">Contribution {formatPercent(driver.contribution_ratio)}</span>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </DisclosurePanel>
        ) : null}

        {findingComparison ? (
          <DisclosurePanel title="Recent trend" className="detail-section">
            <ChapterHeader kicker="Trend" title="Recent Trend" />
            <div className="detail-card finding-detail-trend-card">
              <div className="workflow-mode-header">
                <div>
                  <span className="summary-card-label">Evidence history</span>
                  <strong>{metricHistory?.label || signalDisplayName({ metric_name: findingComparison.comparison_metric_name })}</strong>
                  <p>{metricHistory?.summary || comparisonTrendSummary(findingComparison)}</p>
                </div>
              </div>
              {metricHistoryCaveatLabel ? (
                <div className="metric-history-status-row">
                  <span className="chip">{metricHistoryCaveatLabel}</span>
                </div>
              ) : null}
              {metricHistory?.points?.length ? (
                <MetricSparkline
                  points={metricHistory.points}
                  width={1200}
                  height={150}
                  padding={18}
                  ariaLabel={`${metricHistory.label}: ${metricHistory.summary}`}
                />
              ) : null}
              <p className="overview-note">Trend history orients review; it does not prove causality or statistical significance.</p>
              {findingComparison.comparison_available ? (
                <div className="finding-meta-grid">
                  <div className="finding-meta-item">
                    <span>Previous</span>
                    <strong>{formatSignalReading(findingComparison?.baseline?.metric_value, findingComparison?.unit)}</strong>
                  </div>
                  <div className="finding-meta-item">
                    <span>Current</span>
                    <strong>{formatSignalReading(findingComparison?.current?.metric_value, findingComparison?.unit)}</strong>
                  </div>
                  {findingComparison.window ? (
                    <div className="finding-meta-item">
                      <span>Runs compared</span>
                      <strong>
                        {findingComparison.window.baseline_run_count} baseline · {findingComparison.window.current_run_count} current
                      </strong>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {metricHistory?.points?.length ? (
                <div className="metric-history-point-list">
                  {metricHistory.points.map((point) => {
                    const caveat = trendCaveatLabel(point);
                    return (
                      <div className="metric-history-point-row" key={point.analysis_run_id}>
                        <span>{formatHistoryDate(point.analysed_at)}</span>
                        <strong>{formatSignalReading(point.metric_value, point.unit)}</strong>
                        <span>
                          Runs {point.run_count} · {point.analysis_version || 'analysis n/a'}
                          {caveat ? ` · ${caveat}` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {metricHistory?.events?.length ? (
                <div className="metric-history-events">
                  <span className="summary-card-label">Review markers</span>
                  {metricHistory.events.map((event) => (
                    <div className="metric-history-event" key={`${event.event_type}-${event.related_id || event.analysis_run_id || event.occurred_at}`}>
                      <strong>{event.label}</strong>
                      <span>{formatHistoryDate(event.occurred_at)}</span>
                      <p>{event.summary}</p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </DisclosurePanel>
        ) : null}
        </div>
      </div>
    </section>
  );
}
